import { useState, useRef, useEffect, useCallback } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import * as pdfjsLib from "pdfjs-dist";
import { useApp } from "../../../context/formflow/AppContext";
import { FIELD_TYPES } from "../../../constants/formflow/fieldTypes";
import { genId, getFileUrl } from "../../../utils/formflow/helpers";
import { autoDetectFields, extractFieldFontSizes } from "../../../utils/formflow/pdfDetect";
import { BuilderTopBar } from "./components/BuilderTopBar";
import { BuilderLeftPanel } from "./components/BuilderLeftPanel";
import { BuilderCenterPanel } from "./components/BuilderCenterPanel";
import { BuilderRightPanel } from "./components/BuilderRightPanel";
import axiosInstance from "../../../api/axiosInstance";

pdfjsLib.GlobalWorkerOptions.workerSrc = "https://unpkg.com/pdfjs-dist@3.11.174/build/pdf.worker.min.js";

export function Builder() {
  const navigate = useNavigate();
  const location = useLocation();
  const { showToast } = useApp();

  const initialForm = location.state?.form || null;

  const [pages, setPages] = useState([]);
  const [form, setForm] = useState(
    initialForm || {
      id: null,
      name: "Untitled Form",
      pdfName: null,
      pdfData: null,
      roles: [{ id: null, name: "Signer 1" }],
      fields: [],
      createdAt: new Date().toISOString(),
    },
  );
  const [activeRole, setActiveRole] = useState(form.roles?.[0]?.id);
  const [activeType, setActiveType] = useState(null);
  const [selField, setSelField] = useState(null);
  const [drag, setDrag] = useState(null);
  const [editName, setEditName] = useState(false);

  const containerRef = useRef(null);

  const selectedField = form.fields?.find((f) => f.id === selField);
  const currentRole = form.roles?.find((r) => r.id === activeRole);

  // ── Render PDF pages to canvas images ────────────────────────────────────
  const renderPDF = async (url) => {
    try {
      const res = await fetch(url);
      if (!res.ok) throw new Error("PDF not accessible");

      const buffer = await res.arrayBuffer();
      const pdf = await pdfjsLib.getDocument({ data: buffer.slice(0) }).promise;

      const allPages = [];
      for (let i = 1; i <= pdf.numPages; i++) {
        const page = await pdf.getPage(i);
        const viewport = page.getViewport({ scale: 1.5 });

        const canvas = document.createElement("canvas");
        const ctx = canvas.getContext("2d");
        canvas.width = viewport.width;
        canvas.height = viewport.height;

        await page.render({ canvasContext: ctx, viewport }).promise;
        allPages.push(canvas.toDataURL("image/png"));
      }

      setPages(allPages);
    } catch (err) {
      console.error("PDF render error:", err);
      showToast("Failed to render PDF", "error");
    }
  };

  // ── Upload PDF + auto-detect fields ──────────────────────────────────────
  const handlePdfUpload = async (file) => {
    try {
      if (!file || file.type !== "application/pdf") {
        showToast("Please upload a valid PDF file", "error");
        return;
      }

      // Step 1 — Upload to backend
      const formData = new FormData();
      formData.append("file", file);

      const { data } = await axiosInstance.post("api/formflow/forms", formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });

      if (!data.success) {
        showToast(data.message || "Upload failed", "error");
        return;
      }

      const { form: backendForm } = data;
      const defaultRoleId = backendForm.roles?.[0]?.id;

      // Step 2 — Read file once; clone for each consumer so neither detaches
      //          the shared buffer (fixes "detached ArrayBuffer" error)
      const arrayBuffer = await file.arrayBuffer();

      // Step 3 — Extract text items + font sizes per page (pdfjs)
      const textItemsByPage = await extractFieldFontSizes(arrayBuffer.slice(0));

      // Step 4 — Auto-detect AcroForm fields (pdf-lib)
      const detectedFields = await autoDetectFields(arrayBuffer.slice(0), defaultRoleId, textItemsByPage);

      // Step 5 — Merge into form state
      setForm((prev) => ({
        ...prev,
        id: backendForm.id,
        name: backendForm.name,
        pdfFileId: backendForm.file.id,
        pdfName: backendForm.file.original_name,
        pdfData: getFileUrl(backendForm.file.public_url),
        file: backendForm.file,
        roles: backendForm.roles,
        fields: [...prev.fields, ...detectedFields],
      }));

      setActiveRole(defaultRoleId);

      // Step 6 — Render pages
      renderPDF(getFileUrl(backendForm.file.public_url));

      navigate(`/formflow/builder?id=${backendForm.id}`, { replace: true });

      if (detectedFields.length > 0) {
        showToast(`PDF uploaded · ${detectedFields.length} field${detectedFields.length !== 1 ? "s" : ""} auto-detected`, "success");
      } else {
        showToast("PDF uploaded · No existing fields found — add them manually", "info");
      }
    } catch (err) {
      console.error("handlePdfUpload error:", err);
      showToast(err?.response?.data?.message || "Failed to upload PDF", "error");
    }
  };

  // ── Load existing form by URL param ──────────────────────────────────────
  useEffect(() => {
    const loadForm = async () => {
      const params = new URLSearchParams(location.search);
      const formId = params.get("id");
      if (!formId) return;

      try {
        const { data } = await axiosInstance.get(`/api/formflow/forms/${formId}`);
        if (!data.success) {
          showToast("Failed to load form", "error");
          return;
        }

        const loadedForm = data.form;
        setForm(loadedForm);
        setActiveRole(loadedForm.roles?.[0]?.id || null);
      } catch (err) {
        console.error(err);
        showToast(err?.response?.data?.message || "Failed to load form", "error");
      }
    };

    loadForm();
  }, []);

  // ── Render PDF whenever file changes ─────────────────────────────────────
  useEffect(() => {
    if (form.file?.public_url) {
      renderPDF(getFileUrl(form.file.public_url));
    }
  }, [form.file]);

  // ── Field CRUD ────────────────────────────────────────────────────────────
  const updateField = (id, patch) =>
    setForm((f) => ({
      ...f,
      fields: f.fields.map((field) => (field.id === id ? { ...field, ...patch } : field)),
    }));

  const deleteField = (id) => {
    setForm((f) => ({ ...f, fields: f.fields.filter((field) => field.id !== id) }));
    setSelField(null);
  };

  // ── Roles ─────────────────────────────────────────────────────────────────
  const addRole = () => {
    const nr = { id: genId(), name: `Signer ${form.roles.length + 1}` };
    setForm((f) => ({ ...f, roles: [...f.roles, nr] }));
    setActiveRole(nr.id);
  };

  const removeRole = (roleId) => {
    if (form.roles.length === 1) {
      showToast("At least one role required", "error");
      return;
    }
    setForm((f) => ({
      ...f,
      roles: f.roles.filter((r) => r.id !== roleId),
      fields: f.fields.filter((field) => field.roleId !== roleId),
    }));
    if (activeRole === roleId) {
      setActiveRole(form.roles.find((r) => r.id !== roleId)?.id);
    }
  };

  // ── Click to place field ──────────────────────────────────────────────────
  const handleCanvasClick = useCallback(
    (e) => {
      if (!activeType || drag) return;

      const container = containerRef.current;
      if (!container) return;

      const pageEls = container.querySelectorAll("[data-page]");
      if (!pageEls.length) return;

      for (let i = 0; i < pageEls.length; i++) {
        const rect = pageEls[i].getBoundingClientRect();

        if (e.clientY >= rect.top && e.clientY <= rect.bottom) {
          const x = (e.clientX - rect.left) / rect.width;
          const y = (e.clientY - rect.top) / rect.height;
          const ft = FIELD_TYPES.find((f) => f.id === activeType);

          const nf = {
            id: genId(),
            type: activeType,
            roleId: activeRole,
            label: ft.label,
            required: false,
            hiddenUntilSigned: false,
            placeholder: "",
            x: Math.max(0, Math.min(x, 1)) * 100,
            y: Math.max(0, Math.min(y, 1)) * 100,
            w: (ft.dw / 1000) * 100,
            h: (ft.dh / 1000) * 100,
            pageIndex: i,
            fontSize: null, // manually placed — no auto font size
          };

          setForm((f) => ({ ...f, fields: [...f.fields, nf] }));
          setSelField(nf.id);
          setActiveType(null);
          return;
        }
      }
    },
    [activeType, activeRole, drag],
  );

  // ── Drag to reposition field ──────────────────────────────────────────────
  const handleFieldDown = useCallback(
    (e, fieldId) => {
      e.stopPropagation();
      setSelField(fieldId);
      if (activeType) return;
      setDrag({ fieldId, startX: e.clientX, startY: e.clientY });
    },
    [activeType],
  );

  useEffect(() => {
    if (!drag) return;

    const move = (e) => {
      const container = containerRef.current;
      const pageEls = container.querySelectorAll("[data-page]");
      if (!pageEls.length) return;

      for (let i = 0; i < pageEls.length; i++) {
        const pageRect = pageEls[i].getBoundingClientRect();

        if (e.clientY >= pageRect.top && e.clientY <= pageRect.bottom) {
          const x = ((e.clientX - pageRect.left) / pageRect.width) * 100;
          const y = ((e.clientY - pageRect.top) / pageRect.height) * 100;

          setForm((f) => ({
            ...f,
            fields: f.fields.map((field) =>
              field.id === drag.fieldId
                ? {
                    ...field,
                    x: Math.max(0, Math.min(x, 100)),
                    y: Math.max(0, Math.min(y, 100)),
                    pageIndex: i,
                  }
                : field,
            ),
          }));
          return;
        }
      }
    };

    const up = () => setDrag(null);
    window.addEventListener("mousemove", move);
    window.addEventListener("mouseup", up);
    return () => {
      window.removeEventListener("mousemove", move);
      window.removeEventListener("mouseup", up);
    };
  }, [drag]);

  // ── Save ──────────────────────────────────────────────────────────────────
  const handleSave = async () => {
    try {
      if (!form.name.trim()) {
        showToast("Please name your form", "error");
        return;
      }
      // console.log({ form });
      const response = await axiosInstance.put(`api/formflow/forms/${form.id}`, { form });

      if (response?.data?.success) {
        showToast("Form saved!", "success");
        navigate("/formflow");
      } else {
        showToast(response?.data?.message || "Failed to save form", "error");
      }
    } catch (err) {
      console.error(err);
      showToast(err?.response?.data?.message || "Something went wrong", "error");
    }
  };

  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <div className="flex flex-col h-screen bg-[#f1f5f9]">
      <BuilderTopBar form={form} setForm={setForm} editName={editName} setEditName={setEditName} onSave={handleSave} onBack={() => navigate("/formflow")} handlePdfUpload={handlePdfUpload} />

      <div className="flex overflow-hidden w-full">
        <BuilderLeftPanel form={form} setForm={setForm} activeRole={activeRole} setActiveRole={setActiveRole} activeType={activeType} setActiveType={setActiveType} addRole={addRole} removeRole={removeRole} />

        <BuilderCenterPanel pages={pages} form={form} activeType={activeType} drag={drag} selField={selField} containerRef={containerRef} handleCanvasClick={handleCanvasClick} handleFieldDown={handleFieldDown} currentRole={currentRole} />

        <BuilderRightPanel selectedField={selectedField} form={form} updateField={updateField} deleteField={deleteField} setSelField={setSelField} selField={selField} />
      </div>
    </div>
  );
}
